/**
 * VR library for build mode, made for hands (big tiles, pinch to pick):
 *
 *  - Studio:  the Studio gallery plus your own finished Studio props.
 *  - Search:  the free model library (Poly Pizza) — tap a topic or type one.
 *  - Create:  admins can start a new Studio prop from VR, pick one of the four
 *             concept images when they arrive, then place the result.
 *
 * Picking a model hands it to the placement ray (point, pinch to drop).
 * Opens in front of you and stays put so it's easy to point at.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { vrColors as colors, noRaycast, Surface } from '../vrUi';
import VRKeyboard, { KEY, type KeyAction } from '../VRKeyboard';
import { useVRBuild, type ContentPlacement } from './vrBuildState';
import { STUDIO_GALLERY } from '../../../config/studioGallery';
import { searchModels, type PolyModel } from '../../../services/polyPizzaApi';
import { importLibraryModel } from '../../../services/libraryImport';
import {
  checkStudioAccess, choose, getJob, listJobs, submit, subscribeJob, isTerminal, type StudioJob,
} from '../../../services/studioService';

type Tab = 'studio' | 'search' | 'create';
interface Tile { key: string; title: string; thumb: string; badge?: string; pick: () => void | Promise<void> }

const W = .86;
const COLS = 4, ROWS = 2, PER = COLS * ROWS;
const TILE_W = (W - .06 - (COLS - 1) * .015) / COLS, TILE_H = .2;
const TOPICS = ['Chair', 'Table', 'Sofa', 'Plant', 'Lamp', 'Tree', 'Car', 'Food'];

// ---- remote thumbnails (CORS-enabled hosts) ----
const texCache = new Map<string, THREE.Texture | null>();
function useRemoteTexture(url: string | null | undefined) {
  const [tex, setTex] = useState<THREE.Texture | null>(() => (url ? texCache.get(url) ?? null : null));
  useEffect(() => {
    if (!url) { setTex(null); return; }
    if (texCache.has(url)) { setTex(texCache.get(url) ?? null); return; }
    let live = true;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(url, (t) => { t.colorSpace = THREE.SRGBColorSpace; texCache.set(url, t); if (live) setTex(t); },
      undefined, () => { texCache.set(url, null); if (live) setTex(null); });
    return () => { live = false; };
  }, [url]);
  return tex;
}

function Button({ label, x, y, w = .14, h = .046, active = false, primary = false, disabled = false, name, onPress }: {
  label: string; x: number; y: number; w?: number; h?: number; active?: boolean; primary?: boolean; disabled?: boolean; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : primary ? (hover ? '#5ff0e3' : colors.teal) : active ? '#124940' : hover ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.01} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.017} maxWidth={w - .012} textAlign="center"
      color={disabled ? colors.muted : primary ? '#04211e' : active ? '#71e2cb' : colors.text}>{label}</Text>
  </group>;
}

function TileView({ tile, x, y, busy }: { tile: Tile; x: number; y: number; busy: boolean }) {
  const [hover, setHover] = useState(false);
  const tex = useRemoteTexture(tile.thumb);
  return <group position={[x, y, .006]} name={`vr-lib-tile-${tile.key}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!busy) void tile.pick(); }}>
    <Surface width={TILE_W} height={TILE_H} color={hover ? colors.hover : colors.card} r={.012} />
    <mesh raycast={noRaycast} position={[0, .022, .003]}>
      <planeGeometry args={[TILE_W - .016, .135]} />
      {/* Keyed: a fresh material per state, or the placeholder's dark colour would tint the image */}
      {tex ? <meshBasicMaterial key="tex" map={tex} toneMapped={false} /> : <meshBasicMaterial key="blank" color="#0a141f" toneMapped={false} />}
    </mesh>
    <Text raycast={noRaycast} position={[0, -.07, .004]} fontSize={.0135} maxWidth={TILE_W - .016} textAlign="center" color={colors.text}>
      {tile.title.length > 26 ? `${tile.title.slice(0, 25)}…` : tile.title}
    </Text>
    {tile.badge && <Text raycast={noRaycast} position={[-TILE_W / 2 + .012, .08, .005]} anchorX="left" fontSize={.011} color={colors.teal}>{tile.badge}</Text>}
  </group>;
}

function Grid({ tiles, page, setPage, busy, top }: { tiles: Tile[]; page: number; setPage: (p: number) => void; busy: boolean; top: number }) {
  const pages = Math.max(1, Math.ceil(tiles.length / PER));
  const shown = tiles.slice(page * PER, (page + 1) * PER);
  return <>
    {shown.map((t, i) => (
      <TileView key={t.key} tile={t} busy={busy}
        x={-W / 2 + .03 + TILE_W / 2 + (i % COLS) * (TILE_W + .015)} y={top - TILE_H / 2 - Math.floor(i / COLS) * (TILE_H + .015)} />
    ))}
    {pages > 1 && <>
      <Button label="‹ Prev" x={-.1} y={top - 2 * TILE_H - .055} w={.12} disabled={page === 0} onPress={() => setPage(page - 1)} />
      <Text raycast={noRaycast} position={[0, top - 2 * TILE_H - .055, .006]} fontSize={.014} color={colors.muted}>{`${page + 1} / ${pages}`}</Text>
      <Button label="Next ›" x={.1} y={top - 2 * TILE_H - .055} w={.12} disabled={page >= pages - 1} onPress={() => setPage(page + 1)} />
    </>}
  </>;
}

export default function VRLibraryPanel() {
  const open = useVRBuild((s) => s.libraryOpen);
  const { camera } = useThree();
  const group = useRef<THREE.Group>(null);
  const placed = useRef(false);

  // Park in front of the player when it opens; it then stays put.
  useFrame(() => {
    if (!group.current) return;
    if (!open) { placed.current = false; return; }
    if (placed.current) return;
    const eye = camera.getWorldPosition(new THREE.Vector3());
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.getWorldQuaternion(new THREE.Quaternion()));
    fwd.y = 0; if (fwd.lengthSq() < 1e-4) fwd.set(0, 0, -1); fwd.normalize();
    group.current.position.copy(eye).addScaledVector(fwd, .95);
    group.current.position.y = eye.y - .12;
    group.current.rotation.set(-.12, Math.atan2(-fwd.x, -fwd.z), 0, 'YXZ');
    placed.current = true;
  });

  return <group ref={group} visible={open}>{open && <LibraryBody />}</group>;
}

function LibraryBody() {
  const close = () => useVRBuild.getState().setLibraryOpen(false);
  const place = (p: ContentPlacement) => { useVRBuild.getState().setPlacing(p); close(); };
  const [tab, setTab] = useState<Tab>('studio');
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [admin, setAdmin] = useState(false);
  useEffect(() => { checkStudioAccess().then((a) => setAdmin(a === 'admin')).catch(() => {}); }, []);

  // ---- Studio tab ----
  const [mine, setMine] = useState<StudioJob[]>([]);
  useEffect(() => {
    listJobs(24).then((jobs) => setMine(jobs.filter((j) => j.kind === 'model' && j.status === 'done' && j.result_glb_url))).catch(() => {});
  }, []);
  const studioTiles = useMemo<Tile[]>(() => {
    const seen = new Set<string>();
    const tiles: Tile[] = [];
    for (const j of mine) {
      seen.add(j.id);
      tiles.push({
        key: j.id, title: j.result_meta?.title || j.prompt, badge: 'YOURS',
        thumb: j.preview_urls[j.preview_urls.length - 1] || j.concept_image_url || '',
        pick: () => place({ url: j.result_glb_url!, filename: (j.result_meta?.title || j.prompt).slice(0, 40), minY: j.result_meta?.bounds?.min?.[1] ?? 0, thumbnailUrl: j.preview_urls[j.preview_urls.length - 1] || null }),
      });
    }
    for (const g of STUDIO_GALLERY) {
      if (seen.has(g.id)) continue;
      tiles.push({ key: g.id, title: g.title, thumb: g.thumb, badge: 'STUDIO', pick: () => place({ url: g.url, filename: g.title, minY: g.minY, thumbnailUrl: g.thumb }) });
    }
    return tiles;
  }, [mine]);

  // ---- Search tab ----
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const [results, setResults] = useState<PolyModel[]>([]);
  const runSearch = async (q: string) => {
    setQuery(q); setTyping(false); setPage(0); setStatus('Searching…'); setResults([]);
    try {
      const { models } = await searchModels(q, 0, 24);
      setResults(models);
      setStatus(models.length ? '' : `Nothing found for “${q}”`);
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Search failed'); }
  };
  const searchTiles: Tile[] = results.map((m) => ({
    key: m.id, title: m.title, thumb: m.thumbnail,
    pick: async () => {
      setBusy(true); setStatus(`Getting “${m.title}”…`);
      try { const url = await importLibraryModel(m); place({ url, filename: m.title, autoFit: true, thumbnailUrl: m.thumbnail }); }
      catch (e) { setStatus(e instanceof Error ? e.message : 'Could not get that model'); }
      finally { setBusy(false); }
    },
  }));

  // ---- Create tab (Studio, admins) ----
  const [prompt, setPrompt] = useState('');
  const [job, setJob] = useState<StudioJob | null>(null);
  // A job started earlier in this session: pick it back up.
  useEffect(() => {
    const id = useVRBuild.getState().studioJobId;
    if (id) getJob(id).then((j) => { if (j) { setJob(j); setTab('create'); } }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!job || isTerminal(job.status)) return;
    return subscribeJob(job.id, setJob);
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const create = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setStatus('Starting…');
    try {
      const id = await submit('model', prompt.trim(), { style: 'stylized-baked', quality: 'standard' });
      useVRBuild.getState().setStudioJobId(id);
      setJob({ id, status: 'queued', progress: 0, stage_message: 'Queued', prompt: prompt.trim(), reference_options: [], preview_urls: [] } as unknown as StudioJob);
      setStatus('');
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Could not start'); }
    finally { setBusy(false); }
  };

  const onKey = (a: KeyAction) => {
    const set = tab === 'create' ? setPrompt : setQuery;
    if (a.type === 'backspace') set((t) => t.slice(0, -1));
    else if (a.type === 'go') { if (tab === 'create') void create(); else if (query.trim()) void runSearch(query.trim()); }
    else set((t) => (t + a.value).slice(0, 160));
  };

  const top = .34, gridTop = .2;
  const showKeyboard = (tab === 'search' && typing) || (tab === 'create' && !job);
  const bottom = showKeyboard ? -.62 : -.36;
  const H = top - bottom;
  const tabs: Tab[] = admin ? ['studio', 'search', 'create'] : ['studio', 'search'];
  const tabLabel = { studio: 'Studio', search: 'Search', create: 'Create (AI)' } as const;
  const field = (text: string, placeholder: string) => <group position={[-.06, .2, .006]}>
    <Surface width={.66} height={.056} color="#0a141f" r={.012} />
    <Text raycast={noRaycast} position={[-.315, 0, .003]} anchorX="left" fontSize={.019} color={text ? colors.text : colors.muted} maxWidth={.63}>
      {text ? (text.length > 52 ? `…${text.slice(-51)}` : text) + '▏' : placeholder}
    </Text>
  </group>;

  return <group onClick={(e) => e.stopPropagation()} name="vr-library-panel">
    <group position={[0, (top + bottom) / 2, 0]}>
      <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
      <Surface width={W} height={H} color={colors.panel} />
    </group>
    <Text raycast={noRaycast} position={[-W / 2 + .03, .305, .006]} anchorX="left" fontSize={.024} color={colors.text}>Library</Text>
    {tabs.map((t, i) => <Button key={t} name={`vr-lib-tab-${t}`} label={tabLabel[t]} x={-.17 + i * .15} y={.305} w={.14} active={tab === t}
      onPress={() => { setTab(t); setPage(0); setStatus(''); setTyping(false); }} />)}
    <Button label="Close" name="vr-lib-close" x={W / 2 - .07} y={.305} w={.1} onPress={close} />

    {tab === 'studio' && <>
      <Text raycast={noRaycast} position={[-W / 2 + .03, .25, .006]} anchorX="left" fontSize={.014} color={colors.muted}>
        Made with GathrXR Studio — pinch one, then point where it goes and pinch again
      </Text>
      <Grid tiles={studioTiles} page={page} setPage={setPage} busy={busy} top={gridTop + .02} />
    </>}

    {tab === 'search' && <>
      {TOPICS.map((t, i) => <Button key={t} name={`vr-lib-topic-${t}`} label={t} x={-W / 2 + .03 + .045 + (i % 8) * .093} y={.25} w={.085} h={.04}
        active={query === t} onPress={() => void runSearch(t)} />)}
      {typing ? field(query, 'Type what you need…') : null}
      {!typing && <Button label="Type a search" x={-W / 2 + .1} y={.2} w={.17} onPress={() => { setTyping(true); setQuery(''); }} />}
      {!typing && <Text raycast={noRaycast} position={[-.2, .2, .006]} anchorX="left" fontSize={.014} color={colors.muted} maxWidth={.6}>
        {status || (results.length ? `${results.length} free models · CC0 / CC-BY from Poly Pizza` : 'Pick a topic or type a search')}
      </Text>}
      {!typing && <Grid tiles={searchTiles} page={page} setPage={setPage} busy={busy} top={gridTop - .03} />}
    </>}

    {tab === 'create' && !job && <>
      {field(prompt, 'Describe a prop, e.g. “a red velvet armchair”')}
      <Button label={busy ? 'Starting…' : 'Create'} primary x={.34} y={.2} w={.12} disabled={!prompt.trim() || busy} onPress={() => void create()} />
      <Text raycast={noRaycast} position={[-W / 2 + .03, .14, .006]} anchorX="left" fontSize={.014} color={status ? '#ffb1ad' : colors.muted} maxWidth={W - .06}>
        {status || 'Studio builds it in Blender in a few minutes. Keep building — you will pick a look, then place it.'}
      </Text>
    </>}
    {tab === 'create' && job && <CreateStatus job={job} onPick={async (i) => { try { await choose(job.id, { choice: i }); } catch (e) { setStatus(e instanceof Error ? e.message : 'Could not pick'); } }}
      onPlace={() => place({ url: job.result_glb_url!, filename: (job.result_meta?.title || job.prompt).slice(0, 40), minY: job.result_meta?.bounds?.min?.[1] ?? 0, thumbnailUrl: job.preview_urls[job.preview_urls.length - 1] || null })}
      onNew={() => { setJob(null); setPrompt(''); useVRBuild.getState().setStudioJobId(null); }} status={status} />}

    {tab === 'search' && typing && status && <Text raycast={noRaycast} position={[-W / 2 + .03, .14, .006]} anchorX="left" fontSize={.014} color={colors.muted}>{status}</Text>}
    {showKeyboard && <group position={[0, -.3 - KEY / 2, .006]}>
      <VRKeyboard onKey={onKey} goLabel={tab === 'create' ? 'Create' : 'Search'} />
    </group>}
  </group>;
}

function CreateStatus({ job, onPick, onPlace, onNew, status }: { job: StudioJob; onPick: (i: number) => void; onPlace: () => void; onNew: () => void; status: string }) {
  const choosing = job.status === 'choosing' && job.reference_options.length > 0;
  const done = job.status === 'done' && !!job.result_glb_url;
  const failed = job.status === 'failed' || job.status === 'canceled';
  const line = failed ? (job.error || 'Studio could not make this one.')
    : done ? 'Ready! Place it in your world.'
      : choosing ? 'Pick the look you like — Studio builds that one.'
        : `${job.stage_message || job.status}${job.progress ? ` · ${Math.round(job.progress)}%` : ''}`;
  const optW = (W - .06 - 3 * .015) / 4;
  return <>
    <Text raycast={noRaycast} position={[-W / 2 + .03, .25, .006]} anchorX="left" fontSize={.016} color={colors.text} maxWidth={W - .06}>
      {`“${job.prompt.slice(0, 70)}”`}
    </Text>
    <Text raycast={noRaycast} position={[-W / 2 + .03, .21, .006]} anchorX="left" fontSize={.015} color={failed || status ? '#ffb1ad' : colors.teal} maxWidth={W - .06}>{status || line}</Text>
    {choosing && job.reference_options.slice(0, 4).map((url, i) => (
      <TileView key={url} busy={false} x={-W / 2 + .03 + optW / 2 + i * (optW + .015)} y={.05}
        tile={{ key: `opt-${i}`, title: `Option ${i + 1}`, thumb: url, pick: () => onPick(i) }} />
    ))}
    {done && <TileView busy={false} x={-W / 2 + .03 + TILE_W / 2} y={.05}
      tile={{ key: 'result', title: job.result_meta?.title || 'Your prop', thumb: job.preview_urls[job.preview_urls.length - 1] || job.concept_image_url || '', pick: onPlace }} />}
    {done && <Button label="Place it" primary x={0} y={.05} w={.16} onPress={onPlace} />}
    {(done || failed) && <Button label="Make another" x={.2} y={.05} w={.16} onPress={onNew} />}
    {!done && !failed && !choosing && <Text raycast={noRaycast} position={[-W / 2 + .03, .1, .006]} anchorX="left" fontSize={.014} color={colors.muted} maxWidth={W - .06}>
      You can close this and keep building — it keeps going. Reopen Library › Create to check on it.
    </Text>}
  </>;
}
